import { useEffect, Fragment, useContext, useState } from 'react';
import styled from '@emotion/styled';
import {css} from '@emotion/react';
import {PrimaryButton, PrimaryOutlineButton} from 'shared/buttons';
import FinalFormNavigation from '../wizard/FinalFormNavigation';
import {NavigationContext} from '../../context/NavigationContext';
import {ConversieButtons} from 'shared/conversie';
import LeadFormFinal from '../wizard/LeadFormFinal';
import {Currency, Heading} from 'shared/components';
import OverviewContainer from './OverviewContainer';
import {resultText} from './../../resultText';
import config, {whiteListUrlParams} from '../../config';
import {ResultContainer} from 'shared/wizard/ToolContainer';
import {NoSavingSvg} from 'images/shared';
import {getParamsFromState} from '../../../../shared/helpers';
import { useNavigate } from 'react-router-dom';


const Results = ({handleSubmit, errors, results, settings, values}) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/hypotheekbespaarcheck/results');
  }, [navigate]);

  const {selectedDetailType, onFinalResultPage} = useContext(NavigationContext);

  return (
    selectedDetailType ? (
      <DetailedOpportunity
        results={results}
        settings={settings}
        onFinalResultPage={onFinalResultPage}
          hideProviders={settings.hideProvidersInResult}
          handleSubmit={handleSubmit}
        />
      ) : (
        <div css={{ maxWidth: '1024px' }}>
          <Heading>Waar kun je op besparen?</Heading>
          <OpportunityContainer>
            {results.result.map(result => (
              <SavingOpportunity key={result.type} {...result} amountOfResults={results.result.length} />
            ))}
          </OpportunityContainer>
          <EstimateDisclaimer>
            {onFinalResultPage
              ? '* De schatting van wat je kunt besparen is nu gebaseerd op je gedetailleerde gegevens.'
              : '* Dit is een geschatte besparing, vul je gegevens aan voor een nauwkeurige berekening.'}
          </EstimateDisclaimer>
          <ConversieButtons
            css={{ marginTop: '24px' }}
            app={config.app}
            settings={settings}
            LeadForm={LeadFormFinal}
            PdfForm={() => <></>}
            handleSubmit={handleSubmit}
            hidePdfButton
            urlLeadFormParams={getParamsFromState(values, whiteListUrlParams)}
          />
        {!selectedDetailType && <FinalFormNavigation handleSubmit={handleSubmit} errors={errors} loading={false} />}
      </div>
    )
  );
};

const DetailedOpportunity = ({
  settings,
  results,
  handleSubmit,
  hideProviders,
  onFinalResultPage,
}) => {
  const { leadFormActive, toggleLeadForm, selectedDetailType } = useContext(NavigationContext);
  const {
    type,
    paybackPeriod,
    firstPayment,
    monthlySaving,
    providerLabel,
  } = results.result.find(({ type }) => type === selectedDetailType);
  const [readMore, setReadMore] = useState(false);
  const footerText = resultText[type]['footer'];
  return (
    <>
      <OverviewContainer settings={settings} {...results} />
      <ResultContainer>
        <DetailedContainer>
          <Heading>Je besparing door {type}</Heading>
          <MonthlySaving block>
            <div css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
              <Currency value={monthlySaving} decimals={0} fontSize="40px" fontWeight={'800'} />
              <sub css={{ marginLeft: '1em' }}>p/m</sub>
            </div>
            <p>Geschatte besparing</p>
          </MonthlySaving>
          <SavingSummary css={{ borderBottom: '1px dashed', borderTop: '1px dashed'  }} hideProviders={hideProviders}>
            <div>
              <h2>{paybackPeriod} maanden</h2>
              <div css={{ fontSize: '12px' }}>Terugverdientijd</div>
            </div>
            <div>
              <Currency value={firstPayment} decimals={0} fontSize="24px" fontWeight="800" />
              <div css={{ fontSize: '12px' }}>{onFinalResultPage ? '1e maandlasten' : '1e maandlasten (excl. aflossing)'}</div>
            </div>
            {!hideProviders && (
              <div>
                <h2>{providerLabel}</h2>
                <div css={{fontSize: '12px'}}>
                  Hypotheekverstrekker
                </div>
              </div>
            )}
          </SavingSummary>
          <SavingExplainationParagraph>{resultText[type]['long']}</SavingExplainationParagraph>
          <OutputFooter>
            <h3>Wat hebben wij berekend?</h3>
            <p>
              {readMore ? footerText : footerText.slice(0, 48).trim() + '...'}
              <button type="button" onClick={() => setReadMore(!readMore)}>
                {readMore ? 'lees minder' : 'lees meer'}
              </button>
            </p>
          </OutputFooter>
        </DetailedContainer>
        <ConversieButtons
          css={{ marginTop: '24px' }}
          app={config.app}
          settings={settings}
          LeadForm={LeadFormFinal}
          handleSubmit={handleSubmit}
          leadFormActive={leadFormActive}
          toggleLeadForm={toggleLeadForm}
        />
      </ResultContainer>
    </>
  );
};

const SavingOpportunity = props => (
  <SavingOpportunityContainer canSave={props.monthlySaving > 0}>
    <h2>{props.type}</h2>
    {props.monthlySaving > 0 ? <CanSave {...props} /> : <CannotSave {...props} />}
  </SavingOpportunityContainer>
);

const CanSave = ({ type, monthlySaving, paybackPeriod, amountOfResults }) => {
  const { setSelectedDetailType } = useContext(NavigationContext);
  return (
    <Fragment>
      <SavingExplainationParagraph short>{resultText[type]['short']}</SavingExplainationParagraph>
      <div css={amountOfResults <= 2 ? {display: 'flex', justifyContent: 'space-around'} : {}}>
        <MonthlySaving>
          <div css={{display: 'flex', justifyContent: 'center', alignItems: 'flex-end'}}>
            <Currency value={monthlySaving} decimals={0} fontSize="40px" fontWeight="800" />
            <sub css={{marginLeft: '1em'}}>p/m</sub>
          </div>
          <p>Geschatte besparing*</p>
        </MonthlySaving>
        <SavingSummary>
          <div>
            <h2>{paybackPeriod} maanden</h2>
            <span>Terugverdientijd</span>
          </div>
        </SavingSummary>
      </div>
      <PrimaryOutlineButton onClick={() => setSelectedDetailType(type)}>Toelichting</PrimaryOutlineButton>
    </Fragment>
  );
};

const CannotSave = ({type}) => {
  const {currentStep, flow, currentStepIndex, updateStepIndex} = useContext(NavigationContext);
  const isOnOutputPage = currentStep.output;
  const isOnFinalPage = currentStepIndex === flow.length - 1;
  const isOnBaseFlowOutpage = isOnOutputPage && !isOnFinalPage;

  return (
    <CannotSaveContainer>
      {isOnBaseFlowOutpage ? (
        <>
          <SavingExplainationParagraph css={{textAlign: 'center'}}>
            Waarschijnlijk kun je niet besparen. Maak een nauwkeurige berekening om te weten
            <br /> of je op {type} kunt besparen
          </SavingExplainationParagraph>
          <PrimaryButton
            onClick={() => updateStepIndex(currentStepIndex + 1)}
            css={{marginTop: 24, padding: '0 8px'}}
          >
            Nauwkeurige berekening
          </PrimaryButton>
        </>
      ) : (
        <>
          <NoSavingSvg/>
          <h3>Geen besparing mogelijk</h3>
        </>
      )}
    </CannotSaveContainer>
  );
};

const CannotSaveContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 12px;
  text-align: center;

  svg {
    display: block;
    width: 28px;
    height: 28px;
    fill: ${props => props.theme.highlightColor};
  }

  > h3 {
    width: 100%;
    margin-top: 24px;
    color: ${props => props.theme.primaryFontColor};
    font-family: ${props => props.theme.fontFamily};
    font-size: 30px;
    line-height: 40px;
  }

  @media ${props => props.theme.tablet} {
    br {
      display: none;
    }
  }
`;

const MonthlySaving = styled.div(
  ({theme: {fontFamily, primaryColor, highlightColor, mobile, primaryFontColor, secondaryFontColor}, block}) => css`
    box-sizing: border-box;
    margin-top: 40px;
    text-align: center;
    font-family: ${fontFamily};
    font-weight: 800;
    background-color: ${block ? primaryColor : 'none'};
    padding: ${block ? '24px' : null};
    border-radius: ${block ? '10px' : null};

    sub {
      font-size: 18px;
      line-height: 14px;
      color: ${block ? secondaryFontColor : highlightColor};
      margin-left: 5px;
      font-weight: 800;
    }

    span {
      color: ${block ? secondaryFontColor : null};
    }

    p {
      margin: 9px 0 0;
      color: ${block ? secondaryFontColor : primaryFontColor};
      font-size: 12px;
      line-height: 14px;
      letter-spacing: -0.5px;
      font-weight: 600;
    }

    @media ${mobile} {
      margin-top: 24px;
      p {
        font-size: 12px;
      }
    }
  `
);

const SavingSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => (props.hideProviders ? 'space-around' : 'space-between')};
  margin: 20px 0;
  padding: 20px 0;
  border-color: ${props => props.theme.borderColor};

  > div {
    color: ${props => props.theme.primaryColor};
    text-align: center;

    &:only-child {
      flex-basis: 100%;
    }
    h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 800;
      line-height: 24px;
    }
    > span {
      font-size: 12px;
      line-height: 17px;
      font-weight: 600;
    }
  }

  @media ${props => props.theme.mobile} {
    flex-wrap: wrap;
    margin: 24px 0;

    > div {
      flex-basis: 100%;
      margin-bottom: 24px;

      &:last-of-type {
        margin-bottom: 0;
      }

      &:only-child {
        h2 {
          font-size: 18px;
        }

        span {
          color: ${props => props.theme.primaryFontColor};
        }
      }
    }
  }
`;

const SavingExplainationParagraph = styled.p`
  color: ${props => props.theme.primaryFontColor};

  width: 100%;
  text-align: ${props => (props.short ? 'center' : 'left')};

  font-family: ${props => props.theme.fontFamily};
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.1px;

  overflow: hidden;

  @media ${props => props.theme.mobile} {
    height: auto;
  }
`;

const OpportunityContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  @media ${props => props.theme.tablet} {
    flex-wrap: wrap;
  }
`;

const SavingOpportunityContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 180px 1 0;
  border-radius: 10px;
  border: 1px solid ${props => props.theme.borderColor};
  background-color: ${props => props.theme.cardColor};
  padding: 32px 20px 20px;
  box-sizing: border-box;
  margin: 0 12px 16px;
  transition: box-shadow 200ms;

  &:first-of-type {
    margin-left: 0;
  }
  &:last-of-type {
    margin-right: 0;
  }

  &:hover {
    border-color: ${props => (props.canSave ? props.theme.cardColor : null)};
    box-shadow: ${props => (props.canSave ? '0 0 16px rgba(0, 0, 0, 0.16)' : null)};
  }

  h2 {
    width: 100%;
    margin-bottom: 22px;
    text-align: center;
    color: ${props => props.theme.primaryColor};

    font-family: ${props => props.theme.fontFamily};

    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.75px;

    &::first-letter {
      text-transform: capitalize;
    }
  }

  hr {
    width: 32px;
    margin: 15px auto;

    height: 2px;
    background: #e3efed;
    border: none;
  }
  @media ${props => props.theme.tablet} {
    flex-basis: 40%;
    &:nth-of-type(odd) {
      margin-left: 0;
    }
    &:nth-of-type(even) {
      margin-right: 0;
    }
  }

  @media ${props => props.theme.mobile} {
    margin: 0 0 12px;
    flex-basis: 100%;

    &:hover {
      border-style: solid;
      box-shadow: none;
    }

    h2 {
      font-size: 18px;
    }
  }
`;

const EstimateDisclaimer = styled.p`
  box-sizing: border-box;
  padding-right: 12px;
  font-size: 12px;
  line-height: 17px;
  font-weight: 600;
  text-align: right;
  color: ${props => props.theme.primaryColor};

  @media ${props => props.theme.mobile} {
    text-align: center;
    padding: 0;
  }
`;

const DetailedContainer = styled.div`
  box-sizing: border-box;
  border-radius: 10px;
  width: 100%;
  border: 1px solid;
  border-color: ${props => props.theme.borderColor};
  background: ${props => props.theme.cardColor};
  padding: 32px;

  hr {
    width: 100%;
    margin: 15px auto;
    border: 1px dashed ${props => props.theme.borderColor};
  }

  ${SavingSummary} {
    h2 {
      font-size: 24px;
    }
  }
`;

const OutputFooter = styled.footer`
  box-sizing: border-box;
  width: calc(100% + 64px);
  padding: 24px 32px;
  transform: translate(-32px, 32px);
  line-height: 22px;
  color: ${props => props.theme.primaryFontColor};
  background-color: ${props => props.theme.secondaryColor};
  border-radius: 0 0 10px 10px;
  margin-top: auto;

  > h3 {
    font-size: 16px;
    color: ${props => props.theme.primaryColor};
    font-weight: 600;
  }
  button {
    font-size: 14px;
    letter-spacing: -0.1px;
    text-decoration: underline;
    margin-left: 4px;
    color: ${props => props.theme.primaryColor};
    cursor: pointer;
  }
  @media ${props => props.theme.mobile} {
    padding: 16px;
    text-align: center;
  }
`;

export default Results;
